import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminNav from "./AdminNav";
import { getCurrentAdminActor } from "../../utils/currentAdmin";
import Pagination from "../Pagination";
import { apiJson } from "../../utils/apiClient";

function AllStudents() {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [missingFileCandidate, setMissingFileCandidate] = useState(null);
  const [deletingStudentId, setDeletingStudentId] = useState(null);
  const rowsPerPage = 50;
  const userRole = localStorage.getItem("userRole");
  const canAccessWaitlist = [
    "administrator",
    "administrata",
    "administration",
    "admin",
    "superadmin",
  ].includes(userRole);

  useEffect(() => {
    document.title = "All Students - Tectigon Academy";

    fetch(`${process.env.REACT_APP_API_URL}/get_students_with_payments.php`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setStudents(data);
          setFilteredStudents(data);

          const allCourses = new Set();
          data.forEach((student) => {
            student.courses.forEach((c) => {
              if (c.title) allCourses.add(c.title);
            });
          });
          setCourses(["All", ...Array.from(allCourses)]);
        } else {
          console.error("Unexpected data:", data);
        }
      })
      .catch((err) => console.error("Failed to fetch students:", err));
  }, []);

  const handleFilterChange = (e) => {
    const selected = e.target.value;
    setSelectedCourse(selected);
    setCurrentPage(1);

    const filtered = students.filter((student) => {
      const fullName = `${student.name || ""} ${student.surname || ""}`.toLowerCase();
      return fullName.includes(searchQuery.toLowerCase());
    });

    if (selected === "All") {
      setFilteredStudents(filtered);
    } else {
      setFilteredStudents(
        filtered.filter((student) =>
          student.courses.some((c) => c.title === selected)
        )
      );
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setCurrentPage(1);

    const filtered = students.filter((student) => {
      const fullName = `${student.name || ""} ${student.surname || ""}`.toLowerCase();
      return fullName.includes(query);
    });

    if (selectedCourse !== "All") {
      setFilteredStudents(
        filtered.filter((student) =>
          student.courses.some((c) => c.title === selectedCourse)
        )
      );
    } else {
      setFilteredStudents(filtered);
    }
  };

  const generateCsv = () => {
    const params = new URLSearchParams();

    if (selectedCourse && selectedCourse !== "All") {
      params.set("course", selectedCourse);
    }

    if (searchQuery.trim()) {
      params.set("search", searchQuery.trim());
    }

    const query = params.toString();
    const link = document.createElement("a");
    link.href = `${process.env.REACT_APP_API_URL}/export_students_csv.php${query ? `?${query}` : ""}`;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const paginatedStudents = filteredStudents.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const totalPages = Math.ceil(filteredStudents.length / rowsPerPage);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, Math.max(1, totalPages)));
  }, [totalPages]);

  const removeStudentFromLists = (studentId) => {
    setStudents((current) => current.filter((student) => student.id !== studentId));
    setFilteredStudents((current) => current.filter((student) => student.id !== studentId));
  };

  const submitStudentDeletion = async (student, action, certificateCount, confirmMissingFiles = false) => {
    if (action === "permanent" && certificateCount > 0 && !confirmMissingFiles) {
      const confirmed = window.confirm(
        `Are you sure you want to permanently delete this student and all ${certificateCount} certificates? The exact certificate files will be moved to quarantine.`
      );
      if (!confirmed) return;
    }

    setDeletingStudentId(student.id);
    try {
      const { response, data } = await apiJson("delete_student.php", {
        method: "POST",
        body: {
          id: student.id,
          action,
          expectedCertificateCount: certificateCount,
          confirmMissingFiles,
          actor: getCurrentAdminActor(),
        },
      });
      if (!response.ok || !data.success) {
        if (data.requiresMissingFileConfirmation) {
          setMissingFileCandidate({
            student,
            certificateCount: data.certificateCount,
            missingFileCount: data.missingFileCount,
          });
          return;
        }
        if (data.requiresReconfirmation) {
          setDeleteCandidate({ student, certificateCount: data.certificateCount });
        }
        throw new Error(data.error || "Failed to delete student.");
      }
      removeStudentFromLists(student.id);
      setDeleteCandidate(null);
      setMissingFileCandidate(null);
      alert(data.message || "Student deleted successfully.");
    } catch (error) {
      alert(
        error instanceof TypeError
          ? "Could not reach the server. Please check your connection and try again."
          : error.message || "Error occurred while deleting student."
      );
    } finally {
      setDeletingStudentId(null);
    }
  };

  const inspectStudentDeletion = async (student) => {
    setDeletingStudentId(student.id);
    try {
      const { response, data } = await apiJson("delete_student.php", {
        method: "POST",
        body: { id: student.id, action: "inspect", actor: getCurrentAdminActor() },
      });
      if (!response.ok || !data.success) throw new Error(data.error || "Could not inspect student dependencies.");

      if (data.requiresChoice) {
        setDeleteCandidate({ student, certificateCount: data.certificateCount });
      } else if (window.confirm("Are you sure you want to delete this student?")) {
        await submitStudentDeletion(student, "permanent", 0);
      }
    } catch (error) {
      alert(
        error instanceof TypeError
          ? "Could not reach the server. Please check your connection and try again."
          : error.message || "Could not inspect student dependencies."
      );
    } finally {
      setDeletingStudentId(null);
    }
  };

  return (
    <div className="flex gap-4">
      <AdminNav />
      <div className="mt-4 ml-[22%] w-[75%]">
        <div className="flex items-center justify-between border-b-2 border-[#c2c2c2] pb-2 w-[95%]">
          <h1 className="text-2xl font-semibold">All Students</h1>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            {canAccessWaitlist && (
              <>
                <Link to="/Waitlist">
                  <button className="bg-white border border-[#152259] text-[#152259] hover:bg-gray-100 px-4 py-2 rounded">
                    Student Waitlist
                  </button>
                </Link>
                <Link to="/CanceledStudents">
                  <button className="bg-white border border-[#152259] text-[#152259] hover:bg-gray-100 px-4 py-2 rounded">
                    Canceled Students
                  </button>
                </Link>
              </>
            )}
            <Link to="/AddUsers">
              <button className="bg-[#152259] hover:bg-[#152239] text-white px-4 py-2 rounded">
                Add Student
              </button>
            </Link>
          </div>
        </div>

        <div className="my-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-1">
            <label className="font-medium">Filter by Course:</label>
            <select
              value={selectedCourse}
              onChange={handleFilterChange}
              className="border px-3 py-1 rounded"
            >
              {courses.map((course, i) => (
                <option key={i} value={course}>
                  {course}
                </option>
              ))}
            </select>
          </div>

          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-end">
            <input
              type="text"
              placeholder="Search by name or surname"
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full rounded border px-3 py-2 sm:w-80"
            />
            <button
              type="button"
              onClick={generateCsv}
              className="rounded bg-[#152259] px-4 py-2 text-white hover:bg-[#152239]"
            >
              Export CSV
            </button>
          </div>
        </div>

        <table className="w-full mt-4 border-collapse border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 border">Name</th>
              <th className="p-2 border">Phone Number</th>
              <th className="p-2 border">Email</th>
              <th className="p-2 border">Courses</th>
              <th className="p-2 border">Payments</th>
              <th className="p-2 border">Edit</th>
              <th className="p-2 border">Delete</th>
              <th className="p-2 border">Progress</th>
            </tr>
          </thead>
          <tbody>
            {paginatedStudents.map((student) => (
              <tr key={student.id} className="border-b border-gray-300">
                <td className="p-2">{student.name} {student.surname}</td>
                <td className="p-2">{student.phone_number}</td>
                <td className="p-2">{student.email}</td>
                <td className="p-2">
                  {student.courses.length > 0 ? (
                    <ul className="list-disc ml-5">
                      {student.courses.map((c, idx) => (
                        <li key={idx}>{c.title}</li>
                      ))}
                    </ul>
                  ) : (
                    "No courses"
                  )}
                </td>
                <td className="p-2">
                  {student.courses.length > 0 ? (
                    <ul>
                      {student.courses.map((c, idx) => (
                        <li key={idx} className="mb-1">
                          <strong>{c.title}:</strong>{" "}
                          {c.payment_method === "Bank"
                            ? `Bank: ${c.amount_all} EUR`
                            : c.payment_method === "All"
                            ? `Pay all: ${c.amount_all} EUR`
                            : c.payment_method === "Divided"
                            ? `${c.amount_month1} € + ${c.amount_month2} €`
                            : c.payment_method === "POS" || c.payment_method === "Cash"
                            ? `${c.payment_method}: ${c.amount_all} EUR`
                            : c.payment_method === "Did not pay"
                            ? "Did not pay"
                            : c.payment_method === "Free"
                            ? "Free"
                            : "No payment info"}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    "No payments"
                  )}
                </td>
                <td className="p-2">
                  <Link to={`/edit-student/${student.id}`}>
                    <button className="bg-[#152259] hover:bg-[#152239] text-white py-1 px-3 rounded">
                      Edit
                    </button>
                  </Link>
                </td>
                <td className="p-2">
                  <button
                    onClick={() => inspectStudentDeletion(student)}
                    disabled={deletingStudentId !== null}
                    className="bg-red-600 text-white px-3 py-1 rounded disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {deletingStudentId === student.id ? "Checking..." : "Delete"}
                  </button>
                </td>
                <td className="p-2">
                  <Link to={`/student-progress/${student.id}`}>
                    <button className="bg-[#152259] hover:bg-[#152239] text-white py-1 px-3 rounded">
                      Progress
                    </button>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          className="mt-6 justify-center sm:justify-center"
        />

        {deleteCandidate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="student-delete-title">
            <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
              <h2 id="student-delete-title" className="text-xl font-semibold">Delete student</h2>
              <p className="mt-3">
                This student has <strong>{deleteCandidate.certificateCount}</strong> issued certificate{deleteCandidate.certificateCount === 1 ? "" : "s"}.
              </p>
              <div className="mt-4 space-y-3 text-sm text-gray-700">
                <p><strong>Soft Delete Student &amp; Keep Certificates:</strong> hides the student from active workflows while preserving certificates and history.</p>
                <p><strong>Delete Student &amp; Certificates:</strong> permanently removes the student and certificate records and moves the exact certificate files to quarantine.</p>
              </div>
              <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button type="button" onClick={() => setDeleteCandidate(null)} disabled={deletingStudentId !== null} className="rounded border px-4 py-2">Cancel</button>
                <button type="button" onClick={() => submitStudentDeletion(deleteCandidate.student, "soft", deleteCandidate.certificateCount)} disabled={deletingStudentId !== null} className="rounded bg-[#152259] px-4 py-2 text-white disabled:opacity-50">Soft Delete &amp; Keep Certificates</button>
                <button type="button" onClick={() => submitStudentDeletion(deleteCandidate.student, "permanent", deleteCandidate.certificateCount)} disabled={deletingStudentId !== null} className="rounded bg-red-600 px-4 py-2 text-white disabled:opacity-50">Delete Student &amp; Certificates</button>
              </div>
            </div>
          </div>
        )}

        {missingFileCandidate && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="missing-certificate-title">
            <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
              <h2 id="missing-certificate-title" className="text-xl font-semibold">Certificate file missing</h2>
              <p className="mt-3 text-gray-700">
                <strong>{missingFileCandidate.missingFileCount}</strong> of this student&apos;s certificate file{missingFileCandidate.missingFileCount === 1 ? " does" : "s do"} not exist on the server. The certificate record{missingFileCandidate.missingFileCount === 1 ? " is" : "s are"} valid and belong to this student.
              </p>
              <p className="mt-3 text-sm text-gray-700">
                Continuing will delete the missing file&apos;s certificate record, process all existing certificate files normally, and continue deleting the student. No other files will be searched for or removed.
              </p>
              <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button type="button" onClick={() => setMissingFileCandidate(null)} disabled={deletingStudentId !== null} className="rounded border px-4 py-2">Cancel</button>
                <button
                  type="button"
                  onClick={() => submitStudentDeletion(missingFileCandidate.student, "permanent", missingFileCandidate.certificateCount, true)}
                  disabled={deletingStudentId !== null}
                  className="rounded bg-red-600 px-4 py-2 text-white disabled:opacity-50"
                >
                  Continue Without {missingFileCandidate.missingFileCount === 1 ? "Missing File" : `${missingFileCandidate.missingFileCount} Missing Files`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AllStudents;
